import { EmailVerificationCallbackSignInQueryStringName } from "@/components/pages/account/composables/authentication";
import { RouteBuilder } from "@/app/ui/navigation/routeBuilder";

export type SignInQueryOpts = {
  support?: string;
  redirect?: string;
  token?: string | (string | null)[];
};

const builder = new RouteBuilder("Account_");
export const accountRoutes = {
  signIn: builder.route({
    url: "/signin",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "account" */ "@/components/pages/account/LoginPage.vue"
      ),
    componentProps: {
      isSignIn: true,
    },
    preSession: true,
  }),
  signUp: builder.route({
    url: "/signup",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "account" */ "@/components/pages/account/LoginPage.vue"
      ),
    componentProps: {
      isSignIn: false,
    },
    preSession: true,
  }),
  firebaseActionRedirector: builder.route({
    url: "/account/actions",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "account" */ "@/components/pages/account/FirebaseActionRedirector.vue"
      ),
    componentProps: (route) => ({
      isSignIn: route.params.isSignIn !== undefined,
    }),
    preSession: true,
  }),
  verifyEmail: builder.routeWithParams(
    {
      url: "/account/verifyemail",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "account" */ "@/components/pages/account/VerifyEmail.vue"
        ),
      componentProps: (route) => ({
        isSignIn: route.params.isSignIn !== undefined,
      }),
      preSession: true,
    },
    (isSignIn: boolean) => ({ isSignIn: isSignIn.toString() })
  ),
  verifiedEmail: builder.route({
    url: "/account/verifiedemail",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "account" */ "@/components/pages/account/VerifiedEmail.vue"
      ),
    componentProps: (route) => ({
      // The callback url should contain a clue to whether this is an email verification straight after sign in (rather than create).
      isSignIn: Object.keys(route.query).includes(
        EmailVerificationCallbackSignInQueryStringName
      ),
    }),
    preSession: true,
  }),
  tenantGroupSelector: builder.routeWithParams(
    {
      url: "/account/selectcompany",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "account" */ "@/components/pages/account/TenantGroupSelector.vue"
        ),
      componentProps: (route) => {
        return {
          isSignIn: route.params.isSignIn?.toString()?.toLowerCase() === "true",
          isPrefilled:
            route.params.isPrefilled?.toString()?.toLowerCase() === "true",
          redirect: route.params.redirect?.toString()?.toLowerCase(),
          countryCode: route.params.countryCode,
          fullName: route.params.fullName,
        };
      },
      preSession: true,
    },
    (
      isSignIn: boolean,
      isPrefilled: boolean = false,
      redirect: string = "",
      countryCode: string = "",
      fullName: string = ""
    ) => {
      return {
        isSignIn: isSignIn.toString(),
        isPrefilled: isPrefilled.toString(),
        redirect: redirect,
        countryCode,
        fullName,
      };
    }
  ),
  createTenantGroup: builder.routeWithParams(
    {
      url: "/account/create",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "account" */ "@/components/pages/account/CreateTenantGroup.vue"
        ),
      componentProps: (route) => ({
        isPrefilled:
          route.params.isPrefilled?.toString()?.toLocaleLowerCase() === "true",
        countryCode: route.params.countryCode,
        fullName: route.params.fullName,
      }),
      preSession: true,
    },
    (opts?: {
      isPrefilled?: boolean;
      countryCode?: string;
      fullName?: string;
    }) => ({
      isPrefilled: opts?.isPrefilled?.toString() ?? "",
      countryCode: opts?.countryCode ?? "",
      fullName: opts?.fullName ?? "",
    })
  ),
};

export const signInWithQuery = (query: SignInQueryOpts) => ({
  name: accountRoutes.signIn.name,
  query,
});
